/* eslint-disable no-param-reassign */
import * as _ from 'lodash-es';
import moment from 'moment';
import type { Channel } from 'phoenix';
import { Presence } from 'phoenix';
import { Delta } from 'quill/core';
import type { Action } from 'redux';
import type { Draft, PayloadAction, ThunkDispatch } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { AppThunk, RootState } from '../app/redux/getReduxStore';
import type { Banner } from '../components/BannerModalContent';
import { ACTION_TANDEM_ERROR, CATEGORY_PRODUCT } from '../constants/analytics';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { User } from '../graphql/generated/graphql';
import replacePostPath, { getPostPath } from '../post/utils/replacePostPath';
import { editorToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import { track } from '../utils/analytics';
import { isFeatureEnabled } from '../utils/features';
import type useNavigate from '../utils/router/useNavigate';
import type QuillEditor from './QuillEditor';
import SyncEngine from './SyncEngine';
import type Mark from './utils/Mark';
import { transformMark } from './utils/Mark';

const EDITOR_ACTIONS_POST_FRAGMENT = gql(`
  fragment EditorActions_post on PostFields {
    id
    title
    content(isLatest: true) {
      id
      delta
      version
    }
    ... on Post {
      publishedAt
    }
    ...replacePostPath_post
    ...getPostPath_post
  }
`);

export type MagneticWallType = 'comment' | 'annotation' | 'reaction';

export type MagneticWallHighlight = {
  id: string;
  type: MagneticWallType;
};

export type MagneticWallFocus = {
  id: string;
  type: MagneticWallType;
  active: boolean;
  hasExpandIntent: boolean;
};

type SyncStatus = 'down' | 'up' | 'syncing';

type RemoteSelections = {
  [userId: string]: Mark;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export type PresenceStateMeta = { edit: boolean; active: boolean };

export interface PresenceStateUser
  extends Pick<User, 'id' | 'name' | 'avatar'> {
  index: number;
}

export interface PresenceState {
  metas: PresenceStateMeta[];
  user: PresenceStateUser;
}

export enum LockState {
  Timeout = 'timeout',
  UnrecoverableError = 'unrecoverableError',
}

type FocusActionPayload = {
  id: string;
  type: MagneticWallType;
  isActive: boolean;
  hasExpandIntent: boolean;
};

type SyncState = {
  version: number;
  unsaved: Delta;
};

type EditorState = {
  title: string;
  syncStatus: SyncStatus;

  isPickingReaction: boolean;

  magneticWallHighlight: MagneticWallHighlight | null;
  magneticWallFocus: MagneticWallFocus | null;

  currentPostBanner: Banner | null;
  isPositioningPostBanner: boolean;
  isPostBannerPopOverOpen: boolean;

  channel: Channel | null;
  editable: boolean;
  lockState: LockState | null;
  postId: string | null;
  quill: QuillEditor | null;
  readOnly: boolean;
  sync: SyncEngine | null;
  pendingTransmitRange: Mark | null;
  version: number;
  unsaved: Delta;
  initialContent: Delta | null;

  presence: { [userId: string]: PresenceState };
  remoteSelections: RemoteSelections;
  updatedAt: moment.Moment | null;
};

const initialState: EditorState = {
  title: '',
  syncStatus: 'down', // Controlled by sync engine

  isPickingReaction: false,

  magneticWallHighlight: null,
  magneticWallFocus: null,

  currentPostBanner: null,
  isPositioningPostBanner: false,
  isPostBannerPopOverOpen: false,

  channel: null,
  editable: true, // Inherent editability, ex permissions, cannot change
  lockState: null,
  postId: null,
  quill: null,
  readOnly: true, // User specified editability
  sync: null,
  pendingTransmitRange: null,
  version: -1,
  unsaved: new Delta(),
  initialContent: null,

  presence: {},
  remoteSelections: {},
  updatedAt: null,
};

const leave = (): AppThunk => (dispatch, getState) => {
  const { editor } = getState();
  if (editor.channel) {
    editor.channel.leave();
  }
  dispatch(EditorActions.leaveDone());
};

const load =
  (payload: {
    post: DocumentType<typeof EDITOR_ACTIONS_POST_FRAGMENT>;
    isEditable: boolean;
    isDefaultReadOnly?: boolean;
    onRemoteDelta: (delta: Delta) => void;
    isPublicView: boolean;
    navigate?: ReturnType<typeof useNavigate>;
  }): AppThunk =>
  (dispatch, getState) => {
    const { editor, session } = getState();
    const oldChannel = editor.channel;
    if (oldChannel) {
      oldChannel.leave();
    }
    const {
      post,
      isEditable,
      isDefaultReadOnly,
      onRemoteDelta,
      navigate,
      isPublicView,
    } = payload;

    if (session.socket == null) {
      return;
    }

    const channel = session.socket.channel(`posts:${post.id}`);

    const initialContent = new Delta(post.content.delta);
    const initialVersion = post.content.version;

    const sync = createSyncEngine({
      channel,
      initialVersion,
      initialContent,
      onRemoteDelta,
      dispatch,
      getState,
      isPublicView,
    });

    dispatch(
      EditorActions.loadDone({
        channel,
        sync,
        initialContent,
        editable: isEditable,
        postId: post.id,
        readOnly:
          isDefaultReadOnly ||
          ('publishedAt' in post && post.publishedAt != null) ||
          !isEditable,
        title: post.title,
      }),
    );

    initSyncEngine({
      dispatch,
      sync,
      channel,
      getState,
    });
    const presence = new Presence(channel);
    presence.onSync(() => {
      dispatch(
        EditorActions.updatePresence(
          // @ts-expect-error - state is not in presence object according to types
          presence.state,
        ),
      );
    });
    if (navigate) {
      replacePostPath(post, navigate);
    }
  };

const transmitStateChange =
  (range: Mark | null): AppThunk =>
  (dispatch, getState) => {
    const { channel, readOnly, version, unsaved } = getState().editor;
    let pendingTransmitRange = readOnly ? null : range;
    if (pendingTransmitRange && channel && unsaved.length() === 0) {
      transmitSelection(channel, pendingTransmitRange, version);
      pendingTransmitRange = null;
    }
    dispatch(EditorActions.setPendingTransmitRange(pendingTransmitRange));
  };

const syncStateChange =
  (syncState: SyncState): AppThunk =>
  (dispatch, getState) => {
    dispatch(EditorActions.setSyncState(syncState));
    const { unsaved, version } = syncState;
    const {
      editor: { pendingTransmitRange, channel },
    } = getState();
    if (channel && pendingTransmitRange && unsaved.length() === 0) {
      transmitSelection(channel, pendingTransmitRange, version);
      dispatch(EditorActions.setPendingTransmitRange(null));
    }
  };

const localUpdated =
  (delta: Delta): AppThunk =>
  (dispatch, getState) => {
    const { editor } = getState();
    if (editor.sync) {
      editor.sync.addLocalDelta(delta);
    }
    dispatch(EditorActions.localUpdateDone(delta));
  };

const readOnly =
  (isReadOnly: boolean): AppThunk =>
  (dispatch, getState) => {
    const { editor } = getState();
    if (editor.lockState && !isReadOnly) {
      dispatch(UIActions.clearToasts());
      dispatch(UIActions.pushToast(editorToasts.editorLockedError));
    } else {
      const isReadOnlyBefore = editor.readOnly;
      dispatch(EditorActions.setReadOnly(isReadOnly));
      const isReadOnlyAfter = getState().editor.readOnly;
      if (isReadOnlyBefore !== isReadOnlyAfter) {
        dispatch(updateMeta());
      }
    }
  };

const updateMeta = (): AppThunk => (_dispatch, getState) => {
  const { editor } = getState();
  if (editor.channel) {
    editor.channel.push('meta', { edit: !editor.readOnly });
  }
};

const debounceNavigateToPost = _.debounce((getState, publicPath: boolean) => {
  const { editor } = getState();
  const post = { id: editor.postId, title: editor.title };
  if (post.id) {
    const url = new URL(document.location.href);
    url.pathname = getPostPath(post, publicPath);

    // we want to keep the hash and the query parameters
    const nextPath = url.toString().substring(url.origin.length);

    window.history.replaceState({}, '', nextPath);
  }
}, 1000);

const titleUpdate =
  (title: string, isPublicView: boolean): AppThunk =>
  (dispatch, getState) => {
    dispatch(EditorActions.setTitle(title));
    debounceNavigateToPost(getState, isPublicView);
  };

const editorSlice = createSlice({
  name: 'editorSlice',
  initialState,

  reducers: {
    leaveDone() {
      return initialState;
    },
    loadInitialContent(
      state,
      action: PayloadAction<{
        initialContent: Delta;
        initialVersion: number;
        editable: boolean;
      }>,
    ) {
      if (!state.initialContent || state.version < 0) {
        state.initialContent = new Delta(action.payload.initialContent);
        state.version = action.payload.initialVersion;
        state.editable = action.payload.editable;
      }
    },
    loadDone(
      state,
      action: PayloadAction<{
        channel: Channel;
        sync: SyncEngine;
        initialContent: Delta;
        editable: boolean;
        postId: string;
        readOnly: boolean;
        title: string;
      }>,
    ) {
      const { initialContent, readOnly: isReadOnly, ...rest } = action.payload;
      Object.assign(state, rest);

      if (state.readOnly) {
        // If the post was in edit mode, this action should not change it
        state.readOnly = isReadOnly;
      }
      if (!_.isEqual(state.initialContent, initialContent)) {
        state.initialContent = initialContent;
      }
    },
    editorMounted(state, action: PayloadAction<QuillEditor>) {
      state.quill = action.payload as unknown as Draft<QuillEditor>;
    },
    lock(state, action: PayloadAction<LockState>) {
      // "timeout" should not override "unrecoverableError"
      if (!state.lockState || state.lockState === LockState.Timeout) {
        state.lockState = action.payload;
      }
      state.readOnly = true;
    },
    tryUnlocking(state) {
      if (state.lockState === LockState.Timeout) {
        state.lockState = null;
        if (state.editable) {
          state.readOnly = false;
        }
      }
    },
    setReadOnly(state, action: PayloadAction<boolean>) {
      state.readOnly = action.payload || !!state.lockState || !state.editable;
      state.isPositioningPostBanner = false;
    },
    setPendingTransmitRange(state, action: PayloadAction<Mark | null>) {
      state.pendingTransmitRange = action.payload;
    },
    setSyncState(state, action: PayloadAction<SyncState>) {
      state.version = action.payload.version;
      state.unsaved = action.payload.unsaved;
    },
    setSyncStatus(state, action: PayloadAction<SyncStatus>) {
      // This action added mainly for visibility/debuggability.  It is
      // not the basis for UI feedback about connection status.
      state.syncStatus = action.payload;
    },

    setTitle(state, action: PayloadAction<string>) {
      state.title = action.payload;
    },

    setIsPickingReaction(state, action: PayloadAction<boolean>) {
      state.isPickingReaction = action.payload;
    },

    // Updates
    localUpdateDone(state, action: PayloadAction<Delta>) {
      const delta = action.payload;
      state.updatedAt = moment();
      state.remoteSelections = transformRemoteSelections(
        state.remoteSelections,
        delta,
        true,
      );
    },
    remoteUpdated(state, action: PayloadAction<Delta>) {
      const delta = action.payload;
      state.updatedAt = moment();
      state.remoteSelections = transformRemoteSelections(
        state.remoteSelections,
        delta,
      );
    },
    remoteSelection(
      state,
      action: PayloadAction<{ userId: string; mark: Mark }>,
    ) {
      const { userId, mark } = action.payload;
      const transformed = mark && transformMark(mark, state.unsaved);

      state.remoteSelections = transformed
        ? { ...state.remoteSelections, [userId]: transformed }
        : _.omit(state.remoteSelections, userId);
    },
    updatePresence(state, action: PayloadAction<EditorState['presence']>) {
      // we need to clone the object because immer (used by redux toolkit)
      // has auto freezing enabled by default, which will recursively
      // freeze the object, but that causes issues in phoenix-js
      const presence = _.cloneDeep(action.payload);

      const editors = Object.keys(presence).filter((id) =>
        presence[id]?.metas.some((meta) => meta.edit),
      );

      const nonEditingSelectionIds = Object.keys(state.remoteSelections).filter(
        (id) => !editors.includes(id),
      );

      const remoteSelections = nonEditingSelectionIds.length
        ? _.omit(state.remoteSelections, nonEditingSelectionIds)
        : state.remoteSelections;

      state.presence = presence;
      state.remoteSelections = remoteSelections;
    },

    // Post Banner
    setCurrentPostBanner(state, action: PayloadAction<Banner | null>) {
      state.currentPostBanner = action.payload;
    },
    setIsPositioningPostBanner(state, action: PayloadAction<boolean>) {
      state.isPositioningPostBanner = action.payload;
      state.readOnly = !state.editable; // Turn readOnly mode on if the post is editable
    },
    setIsPostBannerPopOverOpen(state, action: PayloadAction<boolean>) {
      state.isPostBannerPopOverOpen = action.payload;
      state.readOnly = !state.editable; // Turn readOnly mode on if the post is editable
    },

    // Magnetic Wall
    highlightAnnotation(state, action: PayloadAction<string>) {
      state.magneticWallHighlight = {
        id: action.payload,
        type: 'annotation',
      };
    },
    highlightComment(state, action: PayloadAction<string>) {
      state.magneticWallHighlight = {
        id: action.payload,
        type: 'comment',
      };
    },
    highlightReaction(state, action: PayloadAction<string>) {
      state.magneticWallHighlight = {
        id: action.payload,
        type: 'reaction',
      };
    },
    removeMagneticWallHighlight(state) {
      state.magneticWallHighlight = null;
    },

    focusMagneticWall(state, action: PayloadAction<FocusActionPayload>) {
      const magneticWallFocus: MagneticWallFocus = {
        id: action.payload.id,
        type: action.payload.type,
        active: action.payload.isActive ?? false,
        hasExpandIntent: action.payload.hasExpandIntent ?? false,
      };
      if (state.magneticWallFocus) {
        // Don't replace the object, just update the properties so that immer
        // would keep the reference unchanged if the values are the same
        Object.assign(state.magneticWallFocus, magneticWallFocus);
      } else {
        state.magneticWallFocus = magneticWallFocus;
      }
    },

    blurMagneticWall(state) {
      state.magneticWallFocus = null;
    },
  },
});

function compactObject<T>(object: { [key: string]: T | null | undefined }): {
  [key: string]: T;
} {
  const compacted: { [key: string]: T } = {};
  Object.entries(object).forEach(([key, value]) => {
    if (value != null) {
      compacted[key] = value;
    }
  });
  return compacted;
}

function transformRemoteSelections(
  remoteSelections: RemoteSelections,
  delta: Delta,
  priority = false,
) {
  const transformed = compactObject(
    _.reduce<RemoteSelections, { [userId: string]: Mark | null }>(
      remoteSelections,
      (memo, mark, userId) => {
        memo[userId] = transformMark(mark, delta, priority);
        return memo;
      },
      {},
    ),
  );
  return _.isEqual(transformed, remoteSelections)
    ? remoteSelections
    : transformed;
}

const UNRECOVERABLE_ERRORS = [
  'checksum',
  'invalid_composition',
  'not_found',
  'server_behind',
  'unicode_split',
];

let sendUpdateDelay = 200;
if (isFeatureEnabled('devtools')) {
  window.__slab.DEVTOOLS.setSyncEngineDelay = (delay: number) => {
    sendUpdateDelay = delay;
  };

  const refreshPostLayoutDebugging = () => {
    const isEnabled =
      localStorage.getItem('postLayoutDebuggingEnabled') === 'true';
    if (isEnabled) {
      document.body.classList.add('postLayoutDebugging');
    } else {
      document.body.classList.remove('postLayoutDebugging');
    }
  };

  refreshPostLayoutDebugging();

  window.__slab.DEVTOOLS.setPostLayoutDebuggingEnabled = (enabled: boolean) => {
    localStorage.setItem(
      'postLayoutDebuggingEnabled',
      enabled ? 'true' : 'false',
    );
    refreshPostLayoutDebugging();
  };
}

function createSyncEngine({
  channel,
  initialVersion,
  initialContent,
  onRemoteDelta,
  dispatch,
  getState,
  isPublicView,
}: {
  channel: Channel;
  initialVersion: number;
  initialContent: Delta;
  onRemoteDelta: (delta: Delta) => void;
  dispatch: ThunkDispatch<RootState, unknown, Action>;
  getState: () => RootState;
  isPublicView: boolean;
}) {
  const sync = new SyncEngine({
    initialVersion,
    initialChecksum: initialContent.length(),
    sendCommit({ baseVersion, baseChecksum, delta, clientId }) {
      setTimeout(() => {
        channel
          .push('update', {
            version: baseVersion,
            delta,
            checksum: baseChecksum,
            clientId,
          })
          .receive('error', (resp) => {
            if (resp && UNRECOVERABLE_ERRORS.includes(resp.error)) {
              dispatch(EditorActions.lock(LockState.UnrecoverableError));
              track(ACTION_TANDEM_ERROR, {
                category: CATEGORY_PRODUCT,
                label: `update_${resp.error}`,
              });
              sync.setIsConnected(false);
              channel.leave();
            }
          });
      }, sendUpdateDelay);
    },
    sendSync({ baseVersion }) {
      return new Promise((accept, reject) => {
        channel
          .push('sync', { version: baseVersion })
          .receive('ok', ({ revisions }) => {
            accept({
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              revisions: revisions.map((rev: any) => ({
                version: rev.version,
                delta: new Delta(rev.delta),
                clientId: rev.clientId,
                checksum: rev.checksum,
              })),
            });
          })
          .receive('timeout', reject)
          .receive('error', (resp) => {
            if (resp && UNRECOVERABLE_ERRORS.includes(resp.error)) {
              const state = getState().editor;
              if (!state.readOnly) {
                dispatch(
                  UIActions.pushToast(
                    editorToasts.couldNotConnectForEditingError,
                  ),
                );
              }
              dispatch(EditorActions.lock(LockState.UnrecoverableError));
              track(ACTION_TANDEM_ERROR, {
                category: CATEGORY_PRODUCT,
                label: `sync_${resp.error}`,
              });
              sync.setIsConnected(false);
              channel.leave();
            } else {
              // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
              reject();
            }
          });
      });
    },
    onRemoteDelta(delta) {
      onRemoteDelta(delta);
      dispatch(EditorActions.remoteUpdated(delta));

      const { editor } = getState();
      if (editor.quill) {
        const title = editor.quill.getTitle();
        if (title !== editor.title) {
          dispatch(EditorActions.titleUpdate(title, isPublicView));
        }
      }
    },
    onStateChange(syncState) {
      dispatch(EditorActions.syncStateChange(syncState));
    },
    onSyncStatusChange(syncStatus) {
      dispatch(EditorActions.setSyncStatus(syncStatus));
    },
  });
  channel.on('selection', ({ range, userId }) => {
    dispatch(
      EditorActions.remoteSelection({
        userId,
        mark: range,
      }),
    );
  });
  channel.on('update', (payload) => {
    const { version, delta, clientId, checksum } = payload;
    sync.receiveUpdate({
      version,
      delta: new Delta(delta),
      clientId,
      checksum,
    });
  });
  channel.onClose(() => {
    sync.setIsConnected(false);
  });
  channel.onError(() => {
    // Occurs (but not limted to) on network loss
    sync.setIsConnected(false);
  });
  return sync;
}

function initSyncEngine({
  dispatch,
  sync,
  channel,
  getState,
}: {
  dispatch: ThunkDispatch<RootState, unknown, Action>;
  getState: () => RootState;
  sync: SyncEngine;
  channel: Channel;
}) {
  dispatch(EditorActions.setSyncState(sync.getSyncState()));

  channel
    .join()
    .receive('ok', () => {
      sync.setIsConnected(true);
      dispatch(updateMeta());
    })
    .receive('error', () => {
      const { editor } = getState();
      if (!editor.readOnly) {
        dispatch(
          UIActions.pushToast(editorToasts.couldNotConnectForEditingError),
        );
      }
      dispatch(EditorActions.lock(LockState.UnrecoverableError));
      sync.setIsConnected(false);
      channel.leave();
    });
}

function transmitSelection(channel: Channel, range: Mark, version: number) {
  channel.push('selection', { range, version });
}

export const EditorActions = {
  ...editorSlice.actions,
  load,
  leave,
  readOnly,
  transmitStateChange,
  syncStateChange,
  localUpdated,
  titleUpdate,
};

export default editorSlice.reducer;
